import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { getLoginUrl } from "@/const";
import { LIVE } from "@/data/live";
import { party as partyLookup } from "@/lib/parties";
import { APP_VERSION } from "@shared/version";
import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";

// ── Static seat list ──────────────────────────────────────────────────────
const SEATS = [
  { code: "N01", name: "Buloh Kasap" },
  { code: "N02", name: "Jementah" },
  { code: "N03", name: "Pemanis" },
  { code: "N04", name: "Kemelah" },
  { code: "N05", name: "Tenang" },
  { code: "N06", name: "Bekok" },
  { code: "N07", name: "Bukit Kepong" },
  { code: "N08", name: "Bukit Pasir" },
  { code: "N09", name: "Gambir" },
  { code: "N10", name: "Tangkak" },
  { code: "N11", name: "Serom" },
  { code: "N12", name: "Bentayan" },
  { code: "N13", name: "Simpang Jeram" },
  { code: "N14", name: "Bukit Naning" },
  { code: "N15", name: "Maharani" },
  { code: "N16", name: "Sungai Balang" },
  { code: "N17", name: "Semerah" },
  { code: "N18", name: "Sri Medan" },
  { code: "N19", name: "Yong Peng" },
  { code: "N20", name: "Semarang" },
  { code: "N21", name: "Parit Yaani" },
  { code: "N22", name: "Parit Raja" },
  { code: "N23", name: "Penggaram" },
  { code: "N24", name: "Senggarang" },
  { code: "N25", name: "Rengit" },
  { code: "N26", name: "Machap" },
  { code: "N27", name: "Layang-Layang" },
  { code: "N28", name: "Mengkibol" },
  { code: "N29", name: "Mahkota" },
  { code: "N30", name: "Paloh" },
  { code: "N31", name: "Kahang" },
  { code: "N32", name: "Endau" },
  { code: "N33", name: "Tenggaroh" },
  { code: "N34", name: "Panti" },
  { code: "N35", name: "Pasir Raja" },
  { code: "N36", name: "Sedili" },
  { code: "N37", name: "Johor Lama" },
  { code: "N38", name: "Penawar" },
  { code: "N39", name: "Tanjong Surat" },
  { code: "N40", name: "Tiram" },
  { code: "N41", name: "Puteri Wangsa" },
  { code: "N42", name: "Johor Jaya" },
  { code: "N43", name: "Permas" },
  { code: "N44", name: "Larkin" },
  { code: "N45", name: "Stulang" },
  { code: "N46", name: "Perling" },
  { code: "N47", name: "Kempas" },
  { code: "N48", name: "Skudai" },
  { code: "N49", name: "Kota Iskandar" },
  { code: "N50", name: "Bukit Permai" },
  { code: "N51", name: "Bukit Batu" },
  { code: "N52", name: "Senai" },
  { code: "N53", name: "Benut" },
  { code: "N54", name: "Pulai Sebatang" },
  { code: "N55", name: "Pekan Nanas" },
  { code: "N56", name: "Kukup" },
];

const PARTIES = ["BN", "PH", "PN", "MUDA", "BERSAMA", "PSM", "ASLI", "IND", "POAM"];

export default function Admin() {
  const { user, loading, isAuthenticated } = useAuth();
  const [adminAuth, setAdminAuth] = useState<{ authenticated: boolean; name: string } | null>(null);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Check shared admin session on mount
  useEffect(() => {
    fetch("/api/admin-check", { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        setAdminAuth(data);
        setCheckingAdmin(false);
      })
      .catch(() => setCheckingAdmin(false));
  }, []);

  // If Manus OAuth admin is logged in, use that
  if (!loading && isAuthenticated && user && user.role === "admin") {
    return <AdminDashboard userName={user.name ?? user.email ?? "Admin"} />;
  }

  // If shared password session is valid, use that
  if (!checkingAdmin && adminAuth?.authenticated) {
    return <AdminDashboard userName={adminAuth.name ?? "Staff"} />;
  }

  // Still loading either auth check
  if (loading || checkingAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="animate-spin h-8 w-8 border-4 border-gray-300 border-t-red-600 rounded-full" />
      </div>
    );
  }

  // Show password login form
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError("");
    setSubmitting(true);
    try {
      const res = await fetch("/api/admin-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        // Re-check admin session
        const check = await fetch("/api/admin-check", { credentials: "include" }).then(r => r.json());
        setAdminAuth(check);
      } else {
        setLoginError(data.error || "Invalid password");
      }
    } catch {
      setLoginError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-sm mx-auto p-6">
        <div className="text-center mb-8">
          <h1 className="text-lg font-bold text-gray-900">Election Admin</h1>
          <p className="text-sm text-gray-500 mt-1">Staff access only</p>
        </div>
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="w-full rounded border border-gray-300 px-4 py-2.5 text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
              autoFocus
              disabled={submitting}
            />
          </div>
          {loginError && (
            <p className="text-sm text-red-600">{loginError}</p>
          )}
          <button
            type="submit"
            disabled={submitting || !password}
            className="w-full rounded bg-gray-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {submitting ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Admin Dashboard ───────────────────────────────────────────────────────

function AdminDashboard({ userName }: { userName: string }) {
  const [tab, setTab] = useState<"results" | "feed" | "config" | "import">("results");

  const resultsQuery = trpc.election.getResults.useQuery();
  const votesQuery = trpc.election.getVotes.useQuery();
  const feedQuery = trpc.election.getFeed.useQuery();
  const configQuery = trpc.election.getConfig.useQuery();

  const results = resultsQuery.data ?? [];
  const declaredCount = results.filter((r) => r.status === "won" || r.status === "won_unofficial" || r.status === "won_official").length;
  const leadingCount = results.filter((r) => r.status === "leading").length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gray-900 text-white">
        <div className="mx-auto max-w-6xl flex items-center justify-between px-4 py-3">
          <div>
            <h1 className="text-lg font-bold">Election Admin Panel <span className="text-xs font-normal text-gray-400 ml-2">v{APP_VERSION}</span></h1>
            <p className="text-xs text-gray-400">
              Logged in as <span className="text-white font-medium">{userName}</span>
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right text-xs">
              <div>
                <span className="text-green-400 font-bold">{declaredCount}</span> declared ·{" "}
                <span className="text-yellow-400 font-bold">{leadingCount}</span> leading ·{" "}
                <span className="text-gray-400">{56 - declaredCount - leadingCount}</span> pending
              </div>
            </div>
            <a
              href="/"
              target="_blank"
              className="rounded bg-red-600 px-3 py-1.5 text-xs font-bold hover:bg-red-700 transition-colors"
            >
              View Live Site →
            </a>
            <button
              onClick={async () => {
                await fetch("/api/admin-logout", { method: "POST", credentials: "include" });
                window.location.reload();
              }}
              className="rounded bg-gray-700 px-3 py-1.5 text-xs font-medium hover:bg-gray-600 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="mx-auto max-w-6xl flex gap-0 px-4">
          {(
            [
              ["results", "Seat Results"],
              ["import", "⚡ Import"],
              ["feed", "Live Feed"],
              ["config", "Settings"],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                tab === key
                  ? "border-red-600 text-red-600"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-6xl px-4 py-6">
        {tab === "results" && (
          <ResultsTab
            results={results}
            votes={votesQuery.data ?? []}
            onRefresh={() => {
              resultsQuery.refetch();
              votesQuery.refetch();
            }}
          />
        )}
        {tab === "feed" && (
          <FeedTab feed={feedQuery.data ?? []} onRefresh={() => feedQuery.refetch()} />
        )}
        {tab === "import" && (
          <ImportTab onRefresh={() => {
            resultsQuery.refetch();
            votesQuery.refetch();
            feedQuery.refetch();
            configQuery.refetch();
          }} />
        )}
        {tab === "config" && (
          <ConfigTab config={configQuery.data} onRefresh={() => {
            resultsQuery.refetch();
            votesQuery.refetch();
            feedQuery.refetch();
            configQuery.refetch();
          }} />
        )}
      </div>
    </div>
  );
}

// ── Results Tab ───────────────────────────────────────────────────────────

function ResultsTab({
  results,
  votes,
  onRefresh,
}: {
  results: any[];
  votes: any[];
  onRefresh: () => void;
}) {
  const [selectedSeat, setSelectedSeat] = useState("");
  const [status, setStatus] = useState<"pending" | "leading" | "won" | "won_unofficial" | "won_official">("leading");
  const [winnerParty, setWinnerParty] = useState("BN");
  const [winnerSub, setWinnerSub] = useState("");
  const [winnerCandidate, setWinnerCandidate] = useState("");
  const [majority, setMajority] = useState("");
  const [candidateVotesInput, setCandidateVotesInput] = useState<
    { name: string; party: string; sub: string; votes: string }[]
  >([]);

  const upsertResult = trpc.election.upsertResult.useMutation();
  const upsertVotes = trpc.election.upsertVotes.useMutation();
  const deleteResult = trpc.election.deleteResult.useMutation();
  const resetSeatMut = trpc.election.resetSeat.useMutation();
  const [lastSubmission, setLastSubmission] = useState("");

  // When a seat is selected, load its candidates from the static data
  const candidates = useMemo(() => {
    if (!selectedSeat) return [];
    return LIVE.candidatesByContest[selectedSeat] ?? [];
  }, [selectedSeat]);

  // When seat changes, pre-fill the vote inputs
  const handleSeatChange = (code: string) => {
    setSelectedSeat(code);
    const cands = LIVE.candidatesByContest[code] ?? [];
    setCandidateVotesInput(
      cands.map((c) => ({
        name: c.name,
        party: c.party,
        sub: c.sub ?? "",
        votes: "",
      })),
    );
    // Check if this seat already has a result
    const existing = results.find((r) => r.seatCode === code);
    if (existing) {
      setStatus(existing.status as any);
      setWinnerParty(existing.party);
      setWinnerSub(existing.sub ?? "");
      setWinnerCandidate(existing.candidate ?? "");
      setMajority(existing.majority?.toString() ?? "");
      // Load existing votes
      const seatVotes = votes.filter((v) => v.seatCode === code);
      if (seatVotes.length > 0) {
        setCandidateVotesInput(
          cands.map((c) => {
            const v = seatVotes.find((sv) => sv.candidateName === c.name);
            return {
              name: c.name,
              party: c.party,
              sub: c.sub ?? "",
              votes: v?.votes?.toString() ?? "",
            };
          }),
        );
      }
    } else {
      setStatus("leading");
      setWinnerParty(cands[0]?.party ?? "BN");
      setWinnerSub(cands[0]?.sub ?? "");
      setWinnerCandidate(cands[0]?.name ?? "");
      setMajority("");
    }
  };

  const handleSubmit = async () => {
    if (!selectedSeat) {
      toast.error("Please select a seat first.");
      return;
    }

    // Prevent duplicate back-to-back submissions with same data
    const submissionKey = JSON.stringify({
      seat: selectedSeat,
      status,
      party: winnerParty,
      sub: winnerSub,
      candidate: winnerCandidate,
      votes: candidateVotesInput.map((c) => c.votes),
    });
    if (submissionKey === lastSubmission) {
      toast.error("No changes detected. Update the data before saving again.", { duration: 4000 });
      return;
    }

    // Validate: if votes are entered, the selected winner must have the highest vote count
    const filledVotesCheck = candidateVotesInput.filter((c) => c.votes && parseInt(c.votes) > 0);
    if (filledVotesCheck.length > 0) {
      const sorted = [...filledVotesCheck].sort((a, b) => parseInt(b.votes) - parseInt(a.votes));
      const topCandidate = sorted[0];
      if (topCandidate && topCandidate.name !== winnerCandidate) {
        toast.error(
          `Vote mismatch: ${topCandidate.name} has the most votes (${topCandidate.votes}) but you selected ${winnerCandidate} as the winner. Please fix Step 1 or Step 3.`,
          { duration: 8000 }
        );
        return;
      }
    }

    try {
      // Save candidate votes FIRST so auto-feed can read them
      const filledVotes = candidateVotesInput.filter((c) => c.votes);
      if (filledVotes.length > 0) {
        await upsertVotes.mutateAsync({
          seatCode: selectedSeat,
          candidates: candidateVotesInput.map((c) => ({
            candidateName: c.name,
            party: c.party,
            sub: c.sub || undefined,
            votes: c.votes ? parseInt(c.votes) : undefined,
          })),
        });
      }

      // Then save result (this also generates the auto-feed entry)
      // Pass formVotes so the feed text reflects exactly what's in the form right now
      await upsertResult.mutateAsync({
        seatCode: selectedSeat,
        status,
        party: winnerParty,
        sub: winnerSub || undefined,
        candidate: winnerCandidate || undefined,
        majority: undefined,
        formVotes: candidateVotesInput.map((c) => ({
          name: c.name,
          party: c.party,
          sub: c.sub || undefined,
          votes: c.votes ? parseInt(c.votes) : undefined,
        })),
      });

      setLastSubmission(submissionKey);
      toast.success(`${selectedSeat} updated successfully!`);
      onRefresh();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to save result.");
    }
  };

  const handleDelete = async (seatCode: string) => {
    if (!confirm(`Remove result for ${seatCode}?`)) return;
    try {
      await deleteResult.mutateAsync({ seatCode });
      toast.success(`${seatCode} result removed.`);
      onRefresh();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to delete.");
    }
  };

  const handleResetSeat = async (seatCode: string) => {
    if (!confirm(`Reset all data for ${seatCode}? This will clear the result and all vote counts.`)) return;
    try {
      await resetSeatMut.mutateAsync({ seatCode });
      toast.success(`${seatCode} has been reset.`);
      // Clear local form state
      setStatus("leading");
      setWinnerParty("");
      setWinnerSub("");
      setWinnerCandidate("");
      setMajority("");
      const cands = LIVE.candidatesByContest[seatCode] ?? [];
      setCandidateVotesInput(cands.map((c) => ({ name: c.name, party: c.party, sub: c.sub ?? "", votes: "" })));
      onRefresh();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to reset seat.");
    }
  };

  // Build tally from current results
  const tally = useMemo(() => {
    const map: Record<string, { won: number; leading: number }> = {};
    results.forEach((r) => {
      if (!map[r.party]) map[r.party] = { won: 0, leading: 0 };
      if (r.status === "won" || r.status === "won_unofficial" || r.status === "won_official") map[r.party]!.won++;
      if (r.status === "leading") map[r.party]!.leading++;
    });
    return Object.entries(map)
      .map(([p, c]) => ({ party: p, ...c }))
      .sort((a, b) => b.won + b.leading - (a.won + a.leading));
  }, [results]);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
      {/* Entry form */}
      <div className="rounded-lg border bg-white p-5">
        <h2 className="text-lg font-bold mb-4">Enter / Update Seat Result</h2>

        {/* Seat selector */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
            Select Seat
          </label>
          <select
            value={selectedSeat}
            onChange={(e) => handleSeatChange(e.target.value)}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none"
          >
            <option value="">— Choose a seat —</option>
            {SEATS.map((s) => {
              const hasResult = results.some((r) => r.seatCode === s.code);
              return (
                <option key={s.code} value={s.code}>
                  {s.code} · {s.name} {hasResult ? "✓" : ""}
                </option>
              );
            })}
          </select>
        </div>

        {selectedSeat && (
          <>
            {/* Step 1: Who is winning / won? */}
            <div className="mb-5">
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1.5">
                Step 1 — Who is winning?
              </label>
              <p className="text-xs text-gray-400 mb-2">Pick the candidate. Party &amp; component party will auto-fill.</p>
              <div className="space-y-1">
                {candidates.map((c) => {
                  const isSelected = winnerCandidate === c.name;
                  return (
                    <button
                      key={c.name}
                      onClick={() => {
                        setWinnerCandidate(c.name);
                        setWinnerParty(c.party);
                        setWinnerSub(c.sub ?? "");
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded border text-left text-sm transition-all ${
                        isSelected
                          ? "border-red-500 bg-red-50 ring-1 ring-red-500"
                          : "border-gray-200 hover:border-gray-400 hover:bg-gray-50"
                      }`}
                    >
                      <span
                        className="inline-block h-4 w-4 shrink-0 rounded-sm"
                        style={{ background: partyLookup(c.party).color }}
                      />
                      <span className="flex-1 font-medium">{c.name}</span>
                      <span className="text-xs text-gray-500 font-semibold">
                        {c.party}{c.sub ? ` · ${c.sub}` : ""}
                      </span>
                      {isSelected && (
                        <span className="text-red-600 font-bold text-xs">✓</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 2: Status — locked until Step 1 is filled */}
            <div className={`mb-5 ${!winnerCandidate ? "opacity-40 pointer-events-none" : ""}`}>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1.5">
                Step 2 — What's the status?
                {!winnerCandidate && <span className="ml-2 text-[10px] font-normal text-gray-400">(complete Step 1 first)</span>}
              </label>
              <p className="text-xs text-gray-400 mb-2">Is this candidate still in the lead, or has the seat been won?</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setStatus("leading")}
                  disabled={!winnerCandidate}
                  className={`flex-1 flex flex-col items-center gap-1 px-4 py-3 rounded border-2 text-sm font-bold transition-all ${
                    status === "leading"
                      ? "border-yellow-500 bg-yellow-50 text-yellow-700"
                      : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                  }`}
                >
                  <span className="text-lg">●</span>
                  <span>In the Lead</span>
                  <span className="text-[10px] font-normal text-gray-400">Still counting</span>
                </button>
                <button
                  onClick={() => setStatus("won_unofficial")}
                  disabled={!winnerCandidate}
                  className={`flex-1 flex flex-col items-center gap-1 px-4 py-3 rounded border-2 text-sm font-bold transition-all ${
                    status === "won_unofficial"
                      ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                      : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                  }`}
                >
                  <span className="text-lg">✓</span>
                  <span>Won</span>
                  <span className="text-[10px] font-normal text-gray-400">Unofficial</span>
                </button>
                <button
                  onClick={() => setStatus("won_official")}
                  disabled={!winnerCandidate}
                  className={`flex-1 flex flex-col items-center gap-1 px-4 py-3 rounded border-2 text-sm font-bold transition-all ${
                    status === "won_official"
                      ? "border-green-600 bg-green-50 text-green-700"
                      : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                  }`}
                >
                  <span className="text-lg">✓✓</span>
                  <span>Won</span>
                  <span className="text-[10px] font-normal text-gray-400">Official</span>
                </button>
              </div>
            </div>

            {/* Step 3: Candidate votes — locked until Step 2 is confirmed */}
            <div className={`mb-5 ${!winnerCandidate || status === "pending" ? "opacity-40 pointer-events-none" : ""}`}>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1.5">
                Step 3 — Vote counts <span className="font-normal text-gray-400">(fill in as results arrive)</span>
                {(!winnerCandidate || status === "pending") && <span className="ml-2 text-[10px] font-normal text-gray-400">(complete Step 2 first)</span>}
              </label>
              <p className="text-xs text-gray-400 mb-2">Enter each candidate's vote count. You can save without these and add later.</p>
              <div className="space-y-2">
                {candidateVotesInput.map((c, i) => (
                  <div key={c.name} className="flex items-center gap-2">
                    <span
                      className="inline-block h-3 w-3 shrink-0 rounded-sm"
                      style={{ background: partyLookup(c.party).color }}
                    />
                    <span className="flex-1 text-sm truncate">
                      {c.name}{" "}
                      <span className="text-gray-400">
                        ({c.party}
                        {c.sub ? ` · ${c.sub}` : ""})
                      </span>
                    </span>
                    <input
                      type="number"
                      value={c.votes}
                      onChange={(e) => {
                        const updated = [...candidateVotesInput];
                        updated[i] = { ...c, votes: e.target.value };
                        setCandidateVotesInput(updated);
                      }}
                      placeholder="Votes"
                      className="w-24 rounded border border-gray-300 px-2 py-1.5 text-sm text-right focus:border-red-500 focus:outline-none"
                    />
                  </div>
                ))}
              </div>
            </div>


            <div className="flex gap-2">
              <button
                onClick={handleSubmit}
                disabled={upsertResult.isPending || !winnerCandidate}
                className="flex-1 rounded bg-red-600 py-3 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {upsertResult.isPending ? "Saving…" : `Save — ${selectedSeat} → ${winnerCandidate || "(pick a candidate)"}`}
              </button>
              {results.find((r) => r.seatCode === selectedSeat) && (
                <button
                  onClick={() => handleResetSeat(selectedSeat)}
                  className="rounded border border-gray-300 px-4 py-3 text-sm font-bold text-gray-600 hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors"
                >
                  Reset
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Right sidebar: tally + recent results */}
      <div className="space-y-4">
        {/* Tally */}
        <div className="rounded-lg border bg-white p-4">
          <h3 className="text-sm font-bold uppercase text-gray-500 mb-3">Running Tally</h3>
          {tally.length === 0 ? (
            <p className="text-sm text-gray-400">No results entered yet.</p>
          ) : (
            <div className="space-y-2">
              {tally.map((t) => (
                <div key={t.party} className="flex items-center gap-2">
                  <span
                    className="inline-block h-4 w-4 rounded-sm"
                    style={{ background: partyLookup(t.party).color }}
                  />
                  <span className="text-sm font-bold flex-1">{t.party}</span>
                  <span className="text-sm font-bold text-green-600">{t.won}</span>
                  <span className="text-xs text-gray-400">won</span>
                  <span className="text-sm font-bold text-yellow-600">{t.leading}</span>
                  <span className="text-xs text-gray-400">lead</span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 pt-3 border-t text-xs text-gray-500">
            Majority needed: <b>29</b> seats
          </div>
        </div>

        {/* Recent results list */}
        <div className="rounded-lg border bg-white p-4">
          <h3 className="text-sm font-bold uppercase text-gray-500 mb-3">
            Entered Results ({results.length}/56)
          </h3>
          {results.length === 0 ? (
            <p className="text-sm text-gray-400">No results yet.</p>
          ) : (
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {results
                .sort((a, b) => a.seatCode.localeCompare(b.seatCode))
                .map((r) => (
                  <div
                    key={r.seatCode}
                    className="flex items-center gap-2 py-1.5 border-b border-gray-100 last:border-0"
                  >
                    <span
                      className="inline-block h-3 w-3 rounded-sm shrink-0"
                      style={{ background: partyLookup(r.party).color }}
                    />
                    <button
                      onClick={() => handleSeatChange(r.seatCode)}
                      className="flex-1 text-left text-sm hover:text-red-600 transition-colors"
                    >
                      <b>{r.seatCode}</b>{" "}
                      <span className="text-gray-500">
                        {r.candidate ?? r.party}
                      </span>
                    </button>
                    <span
                      className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                        r.status === "won" || r.status === "won_unofficial" || r.status === "won_official"
                          ? "bg-green-100 text-green-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {r.status === "won_unofficial" ? "won (unofficial)" : r.status === "won_official" ? "won (official)" : r.status}
                    </span>
                    <button
                      onClick={() => handleDelete(r.seatCode)}
                      className="text-gray-300 hover:text-red-500 text-xs transition-colors"
                      title="Remove"
                    >
                      ✕
                    </button>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Feed Tab ──────────────────────────────────────────────────────────────

function FeedTab({ feed, onRefresh }: { feed: any[]; onRefresh: () => void }) {
  const [feedText, setFeedText] = useState("");


  const addFeed = trpc.election.addFeed.useMutation();
  const deleteFeed = trpc.election.deleteFeed.useMutation();


  const handleAddFeed = async () => {
    if (!feedText) {
      toast.error("Update text is required.");
      return;
    }
    try {
      // Time and date are auto-set on the server (Malaysian time)
      await addFeed.mutateAsync({
        text: feedText,

      });
      toast.success("Feed entry added!");
      setFeedText("");

      onRefresh();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to add feed entry.");
    }
  };

  const handleDeleteFeed = async (id: number) => {
    if (!confirm("Delete this feed entry?")) return;
    try {
      await deleteFeed.mutateAsync({ id });
      toast.success("Feed entry deleted.");
      onRefresh();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to delete.");
    }
  };

  // Show current Malaysian time as a preview
  const [currentTime, setCurrentTime] = useState("");
  useEffect(() => {
    const update = () => {
      const now = new Date();
      setCurrentTime(
        now.toLocaleTimeString("en-MY", {
          timeZone: "Asia/Kuala_Lumpur",
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        })
      );
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_400px]">
      {/* Entry form */}
      <div className="rounded-lg border bg-white p-5">
        <h2 className="text-lg font-bold mb-4">Add Live Feed Entry</h2>
        <div className="mb-3 rounded bg-gray-50 border border-gray-200 px-3 py-2 flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-500 uppercase">Timestamp</span>
          <span className="text-sm font-bold text-gray-700">{currentTime} (MYT) — auto</span>
        </div>
        <div className="mb-3">
          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
            Update Text
          </label>
          <textarea
            value={feedText}
            onChange={(e) => setFeedText(e.target.value)}
            placeholder="BN retains Kluang with increased majority."
            rows={3}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none resize-none"
          />
        </div>
        <button
          onClick={handleAddFeed}
          disabled={addFeed.isPending || !feedText.trim()}
          className="w-full rounded bg-red-600 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
        >
          {addFeed.isPending ? "Adding…" : "Add to Feed"}
        </button>
        <p className="mt-2 text-[10px] text-gray-400">Time is automatically set to current Malaysian time (MYT) when you submit.</p>
      </div>

      {/* Feed list */}
      <div className="rounded-lg border bg-white p-4">
        <h3 className="text-sm font-bold uppercase text-gray-500 mb-3">
          Feed Entries ({feed.length})
        </h3>
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {feed.map((f) => (
            <div
              key={f.id}
              className="flex items-start gap-2 py-2 border-b border-gray-100 last:border-0"
            >
              <div className="flex-1">
                <div className="text-[10px] font-bold text-gray-400 uppercase">
                  {f.date} · {f.time}
                </div>
                <div className="text-sm">{f.text}</div>
              </div>
              <button
                onClick={() => handleDeleteFeed(f.id)}
                className="text-gray-300 hover:text-red-500 text-xs shrink-0 transition-colors"
                title="Delete"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Config Tab ────────────────────────────────────────────────────────────

function ConfigTab({ config, onRefresh }: { config: any; onRefresh: () => void }) {
  // onRefresh now refetches ALL queries (results, votes, feed, config) so Reset All clears everything
  const [isLive, setIsLive] = useState(config?.isLive ?? 1);
  const [lastUpdated, setLastUpdated] = useState(config?.lastUpdated ?? "");

  const updateConfigMut = trpc.election.updateConfig.useMutation();
  const resetAllMut = trpc.election.resetAll.useMutation();

  const handleSave = async () => {
    try {
      await updateConfigMut.mutateAsync({ isLive, lastUpdated });
      toast.success("Config saved!");
      onRefresh();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to save config.");
    }
  };

  return (
    <div className="max-w-lg rounded-lg border bg-white p-5">
      <h2 className="text-lg font-bold mb-4">Live Settings</h2>

      <div className="mb-4">
        <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
          Live Mode
        </label>
        <div className="flex gap-2">
          <button
            onClick={() => setIsLive(1)}
            className={`px-4 py-2 rounded text-sm font-bold transition-colors ${
              isLive === 1
                ? "bg-red-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            LIVE — On
          </button>
          <button
            onClick={() => setIsLive(0)}
            className={`px-4 py-2 rounded text-sm font-bold transition-colors ${
              isLive === 0
                ? "bg-gray-700 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            Off
          </button>
        </div>
        <p className="mt-1 text-xs text-gray-400">
          When ON, the public site shows the live results view. Turn OFF after the night.
        </p>
      </div>

      <div className="mb-4">
        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
          Last Updated Text
        </label>
        <input
          value={lastUpdated}
          onChange={(e) => setLastUpdated(e.target.value)}
          placeholder="11 July 2026, 10:45 PM — 12 of 56 seats declared."
          className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none"
        />
        <p className="mt-1 text-xs text-gray-400">
          This text appears at the top of the live results bar. Auto-updated when you save a seat result.
        </p>
      </div>

      <button
        onClick={handleSave}
        disabled={updateConfigMut.isPending}
        className="rounded bg-red-600 px-6 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
      >
        {updateConfigMut.isPending ? "Saving…" : "Save Settings"}
      </button>

      {/* Danger Zone */}
      <div className="mt-8 border-t border-red-200 pt-6">
        <h3 className="text-sm font-bold uppercase text-red-600 mb-2">Danger Zone</h3>
        <p className="text-xs text-gray-500 mb-3">
          This will permanently delete all seat results, candidate votes, and live feed entries. Use this to start fresh before election night.
        </p>
        <button
          onClick={async () => {
            if (!confirm("Are you sure you want to RESET ALL election data? This cannot be undone.")) return;
            if (!confirm("FINAL WARNING: All results, votes, and feed entries will be permanently deleted. Continue?")) return;
            try {
              await resetAllMut.mutateAsync();
              toast.success("All election data has been reset.");
              setIsLive(0);
              setLastUpdated("");
              onRefresh();
            } catch (err: any) {
              toast.error(err.message ?? "Failed to reset.");
            }
          }}
          disabled={resetAllMut.isPending}
          className="rounded border-2 border-red-600 bg-white px-6 py-2.5 text-sm font-bold text-red-600 hover:bg-red-600 hover:text-white disabled:opacity-50 transition-colors"
        >
          {resetAllMut.isPending ? "Resetting…" : "⚠️ Reset All Election Data"}
        </button>
      </div>
    </div>
  );
}

// ── Import Tab (Scraper) ─────────────────────────────────────────────────

type PreviewSeat = {
  seatCode: string;
  seatName: string;
  candidates: { party: string; votes: number; percentage: number }[];
  totalVotes: number;
  leadingParty: string;
};

// ── Astro Awani party ID → our short code mapping ──────────────────────────
const ASTRO_PARTY_MAP: Record<number, string> = {
  1: "BN",
  42: "PH",
  55: "PN",
  9: "IND",
  65: "MUDA",
  110: "BERSAMA",
  112: "ASLI",
  21: "PSM",
  3: "DAP",
  12: "PKR",
  75: "AMANAH",
  73: "PH",
  71: "PH",
  34: "BERSATU",
  43: "UMNO",
};

function mapAstroPartyId(id: number, name?: string): string {
  if (ASTRO_PARTY_MAP[id]) return ASTRO_PARTY_MAP[id];
  if (!name) return `PARTY_${id}`;
  const upper = name.toUpperCase();
  if (upper.includes("BARISAN NASIONAL")) return "BN";
  if (upper.includes("PAKATAN HARAPAN")) return "PH";
  if (upper.includes("PERIKATAN NASIONAL")) return "PN";
  if (upper.includes("BEBAS")) return "IND";
  if (upper.includes("MUDA")) return "MUDA";
  if (upper.includes("BERSAMA")) return "BERSAMA";
  if (upper.includes("PSM") || upper.includes("SOSIALIS")) return "PSM";
  if (upper.includes("ORANG ASLI")) return "ASLI";
  return name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 6);
}

function titleCase(str: string): string {
  return str.toLowerCase().split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

/** Parse pasted text client-side using regex patterns */
function parseTextClientSide(text: string): PreviewSeat[] {
  const seats: PreviewSeat[] = [];
  const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
  let currentSeat: { code: string; name: string } | null = null;
  let currentCandidates: { party: string; votes: number; percentage: number }[] = [];

  // Flexible seat header: N01, N.01, N01 -, N01 BULOH KASAP, etc.
  const seatHeaderRegex = /^(N\.?\d+)\s*[-–—:]?\s*(.+)?/i;
  // Party names — comprehensive list for Malaysian elections
  const PARTIES = "PH|BN|PN|BERSAMA|PSM|DAP|UMNO|PAS|PKR|AMANAH|MCA|MIC|MUDA|IND|ASLI|BEBAS|PEJUANG|WARISAN|GERAKAN|PPP|PBS|STAR|PBM|BERSATU|PPBM|KDM|PCS|PAP|INDEPENDENT|PARTI BANGSA MALAYSIA";
  // Match: PARTY votes or PARTY: votes or PARTY - votes or votes PARTY
  const voteLineRegex = new RegExp(`^(${PARTIES})\\s*[-–—:.]?\\s*(\\d[\\d,.]*)`, "i");
  // Also match: "CandidateName (PARTY) 12,345" or "CandidateName (PARTY) - 12,345"
  const candidateLineRegex = new RegExp(`^(.+?)\\s*\\((${PARTIES})\\)\\s*[-–—:.]?\\s*(\\d[\\d,.]*)`, "i");
  // Also match: "12,345 PARTY" (votes first, then party)
  const votesFirstRegex = new RegExp(`^(\\d[\\d,.]*)\\s+(${PARTIES})`, "i");
  // Tab-separated: "PARTY\t12345" or "CandidateName\tPARTY\t12345"
  const tabSeparatedRegex = new RegExp(`^(.+?)\\t+(${PARTIES})\\t+(\\d[\\d,.]*)`, "i");
  const tabPartyVoteRegex = new RegExp(`^(${PARTIES})\\t+(\\d[\\d,.]*)`, "i");

  const flushSeat = () => {
    if (currentSeat && currentCandidates.length > 0) {
      const totalVotes = currentCandidates.reduce((sum, c) => sum + c.votes, 0);
      const sorted = [...currentCandidates].sort((a, b) => b.votes - a.votes);
      seats.push({
        seatCode: currentSeat.code,
        seatName: currentSeat.name || currentSeat.code,
        candidates: currentCandidates,
        totalVotes,
        leadingParty: sorted[0].party,
      });
    }
  };

  const parseVotes = (s: string) => parseInt(s.replace(/[,. ]/g, ""));

  for (const line of lines) {
    // Check if this is a seat header
    const seatMatch = line.match(seatHeaderRegex);
    if (seatMatch) {
      // Make sure it's actually a seat header (N followed by 1-2 digits)
      const code = seatMatch[1].replace(".", "").toUpperCase();
      if (/^N\d{1,2}$/.test(code)) {
        flushSeat();
        currentSeat = { code, name: (seatMatch[2] || code).trim().replace(/^[-–—:]\s*/, "") };
        currentCandidates = [];
        continue;
      }
    }

    if (!currentSeat) continue;

    // Try tab-separated with candidate name: "Name\tPARTY\t12345"
    const tabMatch = line.match(tabSeparatedRegex);
    if (tabMatch) {
      currentCandidates.push({ party: tabMatch[2].toUpperCase(), votes: parseVotes(tabMatch[3]), percentage: 0 });
      continue;
    }

    // Try tab-separated: "PARTY\t12345"
    const tabPVMatch = line.match(tabPartyVoteRegex);
    if (tabPVMatch) {
      currentCandidates.push({ party: tabPVMatch[1].toUpperCase(), votes: parseVotes(tabPVMatch[2]), percentage: 0 });
      continue;
    }

    // Try "CandidateName (PARTY) 12345"
    const candMatch = line.match(candidateLineRegex);
    if (candMatch) {
      currentCandidates.push({ party: candMatch[2].toUpperCase(), votes: parseVotes(candMatch[3]), percentage: 0 });
      continue;
    }

    // Try "PARTY 12345" or "PARTY: 12,345"
    const voteMatch = line.match(voteLineRegex);
    if (voteMatch) {
      currentCandidates.push({ party: voteMatch[1].toUpperCase(), votes: parseVotes(voteMatch[2]), percentage: 0 });
      continue;
    }

    // Try "12345 PARTY" (votes first)
    const vfMatch = line.match(votesFirstRegex);
    if (vfMatch) {
      currentCandidates.push({ party: vfMatch[2].toUpperCase(), votes: parseVotes(vfMatch[1]), percentage: 0 });
      continue;
    }
  }
  flushSeat();
  return seats;
}

/** Fetch Astro Awani results client-side via our proxy */
async function fetchAstroAwaniClientSide(): Promise<PreviewSeat[]> {
  // Fetch via our server proxy to avoid CORS
  const [resultsRes, partyRes] = await Promise.all([
    fetch("/api/proxy-awani?path=data/16/result_state_assembly.json"),
    fetch("/api/proxy-awani?path=data/party.json"),
  ]);

  if (!resultsRes.ok) throw new Error(`Failed to fetch results: ${resultsRes.status}`);
  if (!partyRes.ok) throw new Error(`Failed to fetch party list: ${partyRes.status}`);

  const results: any[] = await resultsRes.json();
  const parties: any[] = await partyRes.json();

  // Build party ID → name map for fallback
  const partyNameMap: Record<number, string> = {};
  for (const p of parties) {
    partyNameMap[p.id] = p.name;
  }

  const seats: PreviewSeat[] = [];
  for (const seat of results) {
    const candidates: { party: string; votes: number; percentage: number }[] = [];
    let totalVotes = 0;

    for (const c of seat.candidates ?? []) {
      if (c.vote > 0) {
        const partyName = mapAstroPartyId(c.party, partyNameMap[c.party]);
        totalVotes += c.vote;
        const percentage = seat.registered_voters > 0
          ? Math.round((c.vote / seat.registered_voters) * 100)
          : 0;
        candidates.push({ party: partyName, votes: c.vote, percentage });
      }
    }

    if (candidates.length === 0) continue;

    const sorted = [...candidates].sort((a, b) => b.votes - a.votes);
    seats.push({
      seatCode: seat.seat_id,
      seatName: titleCase(seat.seat_name),
      candidates,
      totalVotes,
      leadingParty: sorted[0].party,
    });
  }

  return seats;
}

function ImportTab({ onRefresh }: { onRefresh: () => void }) {
  const [mode, setMode] = useState<"url" | "paste">("url");
  const [url, setUrl] = useState("https://pru.astroawani.com/");
  const [pasteText, setPasteText] = useState("");
  const [preview, setPreview] = useState<PreviewSeat[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [fetching, setFetching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState("");
  const [lastImport, setLastImport] = useState<string | null>(null);

  const upsertResult = trpc.election.upsertResult.useMutation();
  const upsertVotes = trpc.election.upsertVotes.useMutation();
  const trpcUtils = trpc.useUtils();

  const handleFetch = async () => {
    setFetching(true);
    setPreview([]);
    setSelected(new Set());
    setLastImport(null);
    try {
      if (mode === "url") {
        let seats: PreviewSeat[] = [];

        // Use tRPC getCachedScrape — goes through /api/trpc which Cloudflare allows.
        // The server pre-fetches Astro Awani every 60s in the background.
        // This query just returns the cached result INSTANTLY (no external call).
        const result = await trpcUtils.election.getCachedScrape.fetch();

        if (result.seats && result.seats.length > 0) {
          seats = result.seats.map((s: any) => ({
            seatCode: s.seatCode,
            seatName: s.seatName,
            candidates: s.candidates,
            totalVotes: s.totalVotes,
            leadingParty: s.leadingParty,
          }));
          const ageSeconds = result.ageSeconds || 0;
          const ageText = ageSeconds < 60 ? `${ageSeconds}s ago` : `${Math.round(ageSeconds / 60)}m ago`;
          toast.success(`Found ${seats.length} seats (cached ${ageText}).`);
        } else if (result.error) {
          toast.error(`Server cache error: ${result.error}. Try again in 60 seconds.`);
        } else {
          toast.error("No seats with votes found. Results may not be available yet.");
        }

        setPreview(seats);
        setSelected(new Set(seats.map((s) => s.seatCode)));
      } else {
        // For paste mode, parse client-side with regex
        const seats = parseTextClientSide(pasteText);
        setPreview(seats);
        setSelected(new Set(seats.map((s) => s.seatCode)));
        if (seats.length === 0) {
          toast.error("Could not parse any results from the text. Try pasting in format: N01 Seat Name / BN 12345 / PH 6789");
        } else {
          toast.success(`Parsed ${seats.length} seats from text.`);
        }
      }
    } catch (err: any) {
      toast.error(err.message ?? "Failed to fetch/parse results.");
    } finally {
      setFetching(false);
    }
  };

  const handleImport = async () => {
    const toImport = preview.filter((s) => selected.has(s.seatCode));
    if (toImport.length === 0) {
      toast.error("No seats selected for import.");
      return;
    }
    if (!confirm(`Import ${toImport.length} seat results? This will update the live database.`)) return;

    setImporting(true);
    setImportProgress("");
    const imported: string[] = [];
    const skipped: { seatCode: string; reason: string }[] = [];
    let count = 0;

    try {
      for (let i = 0; i < toImport.length; i++) {
        const seat = toImport[i];
        setImportProgress(`Importing ${i + 1}/${toImport.length}: ${seat.seatCode}...`);

        // Get our canonical candidates for this seat
        const ourCandidates = LIVE.candidatesByContest[seat.seatCode] ?? [];
        if (ourCandidates.length === 0) {
          skipped.push({ seatCode: seat.seatCode, reason: "No candidates in our lineup" });
          continue;
        }

        // Match scraped party votes to our candidates
        const sortedScraped = [...seat.candidates].sort((a, b) => b.votes - a.votes);
        const winnerParty = sortedScraped[0]?.party;

        // Build candidate votes by matching party
        const candidateVotes = ourCandidates.map((c) => {
          const matchedScraped = seat.candidates.find((sc) => sc.party === c.party);
          return {
            candidateName: c.name,
            party: c.party,
            sub: c.sub ?? undefined,
            votes: matchedScraped?.votes ?? undefined,
          };
        });

        // Find the winner candidate from our list
        const winnerCandidate = ourCandidates.find((c) => c.party === winnerParty);
        if (!winnerCandidate) {
          skipped.push({ seatCode: seat.seatCode, reason: `Leading party ${winnerParty} not found in our candidate list` });
          continue;
        }

        // Build formVotes for feed text generation (same format as manual entry)
        const formVotes = ourCandidates.map((c) => {
          const matchedScraped = seat.candidates.find((sc) => sc.party === c.party);
          return {
            name: c.name,
            party: c.party,
            sub: c.sub ?? undefined,
            votes: matchedScraped?.votes ?? undefined,
          };
        });

        try {
          // Step 1: upsertVotes
          await upsertVotes.mutateAsync({
            seatCode: seat.seatCode,
            candidates: candidateVotes,
          });

          // Step 2: upsertResult (this generates the feed entry with correct convention)
          await upsertResult.mutateAsync({
            seatCode: seat.seatCode,
            status: "leading",
            party: winnerCandidate.party,
            sub: winnerCandidate.sub ?? undefined,
            candidate: winnerCandidate.name,
            formVotes,
          });

          imported.push(seat.seatCode);
          count++;
        } catch (err: any) {
          skipped.push({ seatCode: seat.seatCode, reason: err.message ?? "Failed to save" });
        }
      }

      if (count > 0) {
        toast.success(`Successfully imported ${count} seats: ${imported.join(", ")}`);
      }
      if (skipped.length > 0) {
        for (const s of skipped) {
          toast.warning(`\u26a0\ufe0f ${s.seatCode}: ${s.reason}`, { duration: 10000 });
        }
        setLastImport(`Imported ${count} seats, ${skipped.length} skipped (see warnings) at ${new Date().toLocaleTimeString()}`);
      } else {
        setLastImport(`Imported ${count} seats at ${new Date().toLocaleTimeString()}`);
      }
      setPreview([]);
      setSelected(new Set());
      onRefresh();
    } catch (err: any) {
      toast.error(err.message ?? "Import failed.");
    } finally {
      setImporting(false);
      setImportProgress("");
    }
  };

  const toggleSeat = (code: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === preview.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(preview.map((s) => s.seatCode)));
    }
  };

  return (
    <div className="space-y-6">
      {/* Mode selector */}
      <div className="bg-white rounded-lg border p-4">
        <h2 className="text-sm font-bold uppercase text-gray-700 mb-3">Import Results</h2>
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setMode("url")}
            className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
              mode === "url"
                ? "bg-gray-900 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            🌐 Scrape URL
          </button>
          <button
            onClick={() => setMode("paste")}
            className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
              mode === "paste"
                ? "bg-gray-900 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            📋 Paste Data
          </button>
        </div>

        {mode === "url" ? (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Source URL — pick a source or paste any URL
              </label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                <button onClick={() => setUrl("https://pru.astroawani.com/")} className={`px-2 py-1 text-xs rounded border transition-colors ${url.includes('astroawani') ? 'bg-blue-100 border-blue-400 text-blue-700' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}>Astro Awani</button>
                <button onClick={() => setUrl("https://prn.bernama.com/johor/keputusan/official/index.php")} className={`px-2 py-1 text-xs rounded border transition-colors ${url.includes('bernama') ? 'bg-blue-100 border-blue-400 text-blue-700' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}>BERNAMA/SPR</button>
                <button onClick={() => setUrl("https://pru14.ubah.my/get_johor2026_result.php")} className={`px-2 py-1 text-xs rounded border transition-colors ${url.includes('ubah.my') ? 'bg-blue-100 border-blue-400 text-blue-700' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}>DAP SSE</button>
              </div>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://pru.astroawani.com/ or any results page URL"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
              />
            </div>
            <button
              onClick={handleFetch}
              disabled={fetching || !url.trim()}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {fetching ? "Fetching…" : "Fetch & Preview"}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Paste election results (any format — will be parsed by AI if needed)
              </label>
              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder={`Paste results here. Supported formats:\n\nN12 Bentayan\nPH 6178\nBN 3404\n\nOr any table/text format — AI will parse it.`}
                rows={8}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-y"
              />
            </div>
            <button
              onClick={handleFetch}
              disabled={fetching || !pasteText.trim()}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {fetching ? "Parsing…" : "Parse & Preview"}
            </button>
          </div>
        )}
      </div>

      {/* Preview table */}
      {preview.length > 0 && (
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-gray-700">
              Preview ({preview.length} seats found, {selected.size} selected)
            </h3>
            <div className="flex items-center gap-3">
              <button
                onClick={toggleAll}
                className="text-xs text-blue-600 hover:underline font-medium"
              >
                {selected.size === preview.length ? "Deselect all" : "Select all"}
              </button>
              <button
                onClick={handleImport}
                disabled={importing || selected.size === 0}
                className="rounded bg-green-600 px-4 py-2 text-sm font-bold text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {importing ? "Importing…" : `✓ Confirm Import (${selected.size})`}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-left">
                  <th className="px-2 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={selected.size === preview.length}
                      onChange={toggleAll}
                      className="rounded"
                    />
                  </th>
                  <th className="px-2 py-2 font-semibold text-gray-600">Seat</th>
                  <th className="px-2 py-2 font-semibold text-gray-600">Leading</th>
                  <th className="px-2 py-2 font-semibold text-gray-600">Candidates</th>
                  <th className="px-2 py-2 font-semibold text-gray-600 text-right">Total Votes</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((seat) => {
                  const sorted = [...seat.candidates].sort((a, b) => b.votes - a.votes);
                  return (
                    <tr
                      key={seat.seatCode}
                      className={`border-b hover:bg-gray-50 ${
                        selected.has(seat.seatCode) ? "" : "opacity-40"
                      }`}
                    >
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={selected.has(seat.seatCode)}
                          onChange={() => toggleSeat(seat.seatCode)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <span className="font-bold text-gray-900">{seat.seatCode}</span>{" "}
                        <span className="text-gray-600">{seat.seatName}</span>
                      </td>
                      <td className="px-2 py-2">
                        <span className="inline-block rounded px-2 py-0.5 text-xs font-bold bg-yellow-100 text-yellow-800">
                          {seat.leadingParty}
                        </span>
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex flex-wrap gap-1">
                          {sorted.map((c, i) => (
                            <span
                              key={i}
                              className={`text-xs ${
                                i === 0 ? "font-bold text-gray-900" : "text-gray-500"
                              }`}
                            >
                              {c.party}: {c.votes.toLocaleString()}
                              {i < sorted.length - 1 ? " · " : ""}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-gray-700">
                        {seat.totalVotes.toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Last import status */}
      {lastImport && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
          ✓ {lastImport}
        </div>
      )}

      {/* Help text */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 text-xs text-gray-500 space-y-1">
        <p className="font-semibold text-gray-700">How it works:</p>
        <p>• <b>Scrape URL:</b> Fetches live results from the DAP SSE endpoint and parses the HTML tables.</p>
        <p>• <b>Paste Data:</b> Paste any format of election results — regex patterns are tried first, then AI parses the text.</p>
        <p>• Only seats with votes &gt; 0 are shown. Pending and leading seats are always updated. Confirmed (won) seats are only updated if the winner matches — mismatches are skipped with a warning.</p>
        <p>• Each imported seat auto-generates a live feed entry with the real candidate name from our lineup. Review the preview before confirming.</p>
      </div>
    </div>
  );
}
